import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, FolderKanban, Check, Trash2 } from "lucide-react";
import { PROJECTS, STATUS_LABEL, type ProjectSummary } from "../../data/projects";
import { getProjectAlertCount } from "../../lib/studioFromGraph";
import { env } from "../../lib/env";
import { cls } from "../../lib/cls";

/** User-created projects come from the onboarding agent
 *  (Onboard → Create the project). Merged into the switcher so the
 *  new project is visible everywhere the active project is shown. */
function readUserProjects(): ProjectSummary[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(env.VITE_LS_USER_PROJECTS);
    if (!raw) return [];
    const arr = JSON.parse(raw) as ProjectSummary[];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

/* Project tracker — compact picker mounted in the global header so
   every route (Cockpit · Studio · Integrations · Rules · Project map ·
   Admin) shows the same active project. State lives in localStorage
   under the same key the data layer reads, so picking a project here
   re-derives the live graph used by every screen.

   CLAUDE.md §1 — both localStorage keys come from env, never literal. */

const LS_LEGACY = env.VITE_LS_ACTIVE_PROJECT_LEGACY;
const LS_DATA   = env.VITE_LS_ACTIVE_PROJECT;

function readActiveId(): string {
  // Fallback id = the first project in the seeded list (data-driven,
  // never a literal "p1"); the data file is authoritative.
  const fallback = PROJECTS[0]?.id ?? "";
  if (typeof window === "undefined") return fallback;
  return window.localStorage.getItem(LS_DATA) ?? window.localStorage.getItem(LS_LEGACY) ?? fallback;
}

function writeActiveId(id: string): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LS_DATA, id);
  window.localStorage.setItem(LS_LEGACY, id);
  window.dispatchEvent(new CustomEvent("foyrmind:projectChanged", { detail: { id } }));
}

export function ProjectTracker(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveIdState] = useState<string>(() => readActiveId());
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Sync if another tab / route writes to the key.
  useEffect(() => {
    function onStorage(e: StorageEvent): void {
      if (e.key === LS_DATA || e.key === LS_LEGACY) setActiveIdState(readActiveId());
    }
    function onCustom(): void { setActiveIdState(readActiveId()); }
    window.addEventListener("storage", onStorage);
    window.addEventListener("foyrmind:projectChanged", onCustom as EventListener);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("foyrmind:projectChanged", onCustom as EventListener);
    };
  }, []);

  // Click-outside closes the menu.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const [userProjects, setUserProjects] = useState<ProjectSummary[]>(() => readUserProjects());
  const allProjects = useMemo<ProjectSummary[]>(() => [...userProjects, ...PROJECTS], [userProjects]);
  const active = allProjects.find((p) => p.id === activeId) ?? allProjects[0]!;
  const userProjectIds = useMemo(() => new Set(userProjects.map((p) => p.id)), [userProjects]);

  function removeUserProject(id: string): void {
    if (typeof window === "undefined") return;
    try {
      const next = userProjects.filter((p) => p.id !== id);
      window.localStorage.setItem(env.VITE_LS_USER_PROJECTS, JSON.stringify(next));
      setUserProjects(next);
      // If the active project was just removed, fall back to the
      // first project in the merged list.
      if (id === activeId) {
        const fallback = next[0]?.id ?? PROJECTS[0]?.id ?? "";
        if (fallback) {
          writeActiveId(fallback);
          setActiveIdState(fallback);
        }
      }
    } catch { /* ignore */ }
  }

  // Re-read on project-changed event so Onboard's clear button +
  // upsert updates this list without a hard refresh.
  useEffect(() => {
    function onChanged(): void { setUserProjects(readUserProjects()); }
    window.addEventListener("foyrmind:projectChanged", onChanged as EventListener);
    return () => window.removeEventListener("foyrmind:projectChanged", onChanged as EventListener);
  }, []);

  function pick(id: string): void {
    writeActiveId(id);
    setActiveIdState(id);
    setOpen(false);
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cls(
          "inline-flex items-center gap-2 pl-2 pr-2.5 py-1 rounded-full border bg-cream text-ink-700 text-[11.5px]",
          "border-border/70 hover:border-foyr/40 hover:shadow-sm transition-shadow",
        )}
        title={`Active project · ${active.name}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="grid place-items-center w-4 h-4 rounded-full bg-foyr/10 text-foyr-deep shrink-0">
          <FolderKanban className="w-2.5 h-2.5" />
        </span>
        <span className="font-medium text-ink-950 truncate max-w-[180px]">
          {active.name} <span className="font-normal text-ink-500">· {active.bhk}</span>
        </span>
        <ChevronDown className={cls("w-3 h-3 text-ink-500 transition-transform", open && "rotate-180")} />
      </button>
      {open ? (
        <div
          role="listbox"
          className="absolute right-0 mt-1.5 w-[320px] rounded-2xl border border-border/70 bg-cream shadow-[0_12px_28px_-12px_rgba(0,0,0,0.18)] py-1.5 z-50"
        >
          <div className="px-3 pt-1 pb-1.5 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-ink-500">Your studio</span>
            <span className="text-[10px] text-ink-500 tabular">{allProjects.length} projects</span>
          </div>
          <ul className="max-h-[60vh] overflow-y-auto">
            {allProjects.map((p) => {
              const isActive = p.id === activeId;
              const isUserCreated = userProjectIds.has(p.id);
              return (
                <li key={p.id} className="group/row relative">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => pick(p.id)}
                    className={cls(
                      "w-full text-left px-3 py-2 pr-9 flex items-center gap-2 hover:bg-cream-dark transition-colors",
                      isActive && "bg-cream-dark",
                    )}
                  >
                    <span className={cls(
                      "w-1.5 h-1.5 rounded-full shrink-0",
                      p.status === "active"   ? "bg-fern"
                      : p.status === "draft"   ? "bg-amber-500"
                      : p.status === "on-hold" ? "bg-mulberry"
                      : "bg-ink-300",
                    )} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-semibold text-ink-950 truncate">
                        {p.name} <span className="font-normal text-ink-500">· {p.bhk}</span>
                      </div>
                      <div className="text-[10.5px] text-ink-500 truncate">
                        {p.city} · {p.client} · {STATUS_LABEL[p.status]}
                      </div>
                    </div>
                    {(() => { const live = getProjectAlertCount(p.id); return live > 0 ? (
                      <span className="pill text-[9.5px] bg-foyr/10 border border-foyr/30 text-foyr-deep tabular shrink-0" title="Open alerts on this project">
                        {live}
                      </span>
                    ) : null; })()}
                    {isActive ? <Check className="w-3.5 h-3.5 text-foyr shrink-0" /> : null}
                  </button>
                  {isUserCreated ? (
                    <button
                      type="button"
                      title="Remove this linked project from this device"
                      aria-label="Remove linked project"
                      onClick={(ev) => { ev.stopPropagation(); removeUserProject(p.id); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-6 h-6 rounded-md text-foyr-deep hover:bg-foyr/10 transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {userProjects.length > 0 ? (
            <div className="border-t border-border/60 px-3 py-2 flex items-center justify-between text-[10.5px]">
              <span className="text-ink-500">
                <span className="tabular text-ink-700">{userProjects.length}</span> linked Foyr project{userProjects.length === 1 ? "" : "s"} on this device
              </span>
              <button
                type="button"
                onClick={() => { for (const up of userProjects) removeUserProject(up.id); }}
                className="font-medium text-foyr-deep hover:underline underline-offset-2 inline-flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Clear all
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
